import React, { useState, useEffect } from 'react';
import { adminAPI, DashboardActivity, DashboardMetrics } from '../../services/api';
import './DashboardOverview.css';

type AdminView =
  | 'overview'
  | 'applications'
  | 'users'
  | 'payments'
  | 'documents'
  | 'mandates'
  | 'reports'
  | 'settings';

interface DashboardOverviewProps {
  onNavigate?: (view: AdminView) => void;
}

interface MetricCard {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral';
  icon: string;
  view?: AdminView;
}

interface RecentActivity {
  id: string;
  type: 'application' | 'payment' | 'verification';
  description: string;
  time: string;
  status: 'success' | 'warning' | 'error' | 'info';
  view?: AdminView;
}

function formatRelativeTime(value?: string): string {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(value).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
}

function activityLabel(status: string): string {
  switch (status) {
    case 'success': return 'Done';
    case 'warning': return 'Check';
    case 'error': return 'Failed';
    default: return 'New';
  }
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ onNavigate }) => {
  const [metrics, setMetrics] = useState<MetricCard[]>([]);
  const [liveActivity, setLiveActivity] = useState<RecentActivity[]>([]);
  const [pendingApplications, setPendingApplications] = useState(0);
  const [pendingDocuments, setPendingDocuments] = useState(0);
  const [failedPayments, setFailedPayments] = useState(0);
  const [pendingMandates, setPendingMandates] = useState(0);
  const [applicationVolume, setApplicationVolume] = useState<Array<{ label: string; count: number }>>([]);
  const [repaymentStatus, setRepaymentStatus] = useState({ repaid: 0, overdue: 0, defaulted: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatZar = (amount: number) =>
    `R${Math.round(amount || 0).toLocaleString('en-ZA')}`;

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getDashboardOverview();

      if (response.success && response.data) {
        const dashboardData: DashboardMetrics = response.data;
        const activeLoans =
          dashboardData.activeLoans ?? dashboardData.approvedApplications ?? 0;
        const outstanding =
          dashboardData.outstandingAmount ?? dashboardData.totalLoanAmount ?? 0;

        setPendingApplications(dashboardData.pendingApplications || 0);
        setPendingDocuments(dashboardData.pendingDocuments || 0);
        setFailedPayments(dashboardData.failedPayments || 0);
        setPendingMandates(dashboardData.pendingMandates || 0);
        setApplicationVolume(dashboardData.applicationVolume || []);
        setRepaymentStatus(dashboardData.repaymentStatus || { repaid: 0, overdue: 0, defaulted: 0 });
        setLiveActivity(
          (dashboardData.recentActivity || []).map((item: DashboardActivity) => ({
            id: item.id,
            type: item.type,
            description: item.description,
            time: formatRelativeTime(item.occurredAt),
            status: item.status,
            view: item.view,
          }))
        );
        setMetrics([
          {
            title: 'Total Applications',
            value: (dashboardData.totalApplications || 0).toString(),
            change: dashboardData.pendingApplications ? `${dashboardData.pendingApplications} pending review` : '',
            trend: 'neutral',
            icon: 'applications',
            view: 'applications',
          },
          {
            title: 'Active Loans',
            value: activeLoans.toString(),
            change: 'Currently disbursed',
            trend: 'neutral',
            icon: 'money',
            view: 'applications',
          },
          {
            title: 'Outstanding Balance',
            value: formatZar(outstanding),
            change: activeLoans ? `Across ${activeLoans} active loan${activeLoans === 1 ? '' : 's'}` : '',
            trend: 'neutral',
            icon: 'payments',
            view: 'payments',
          },
          {
            title: 'Approval Rate',
            value: `${Number(dashboardData.approvalRate || 0).toFixed(1)}%`,
            change: '',
            trend: 'neutral',
            icon: 'percentage',
            view: 'reports',
          },
        ]);
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      const status = err?.response?.status;
      if (status === 429) {
        setError('Too many requests — wait a moment and retry.');
      } else if (status === 401) {
        setError('Session expired — please log in again.');
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to fetch dashboard data'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Mount-only fetch; retry button calls fetchDashboardData directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kept as a sample / layout preview alongside live metrics
  const sampleMetrics: MetricCard[] = [
    {
      title: 'Total Applications',
      value: '21',
      change: '',
      trend: 'neutral',
      icon: 'applications',
    },
    {
      title: 'Active Loans',
      value: '24',
      change: '',
      trend: 'neutral',
      icon: 'money',
    },
    {
      title: 'Outstanding Amount',
      value: 'R18,200',
      change: '',
      trend: 'neutral',
      icon: 'payments',
    },
    {
      title: 'Approval Rate',
      value: '86%',
      change: '',
      trend: 'neutral',
      icon: 'percentage',
    },
  ];

  const sampleRecentActivity: RecentActivity[] = [
    {
      id: '1',
      type: 'application',
      description: 'New loan application from Thabo Mthembu (R450)',
      time: '5 minutes ago',
      status: 'info'
    },
    {
      id: '2',
      type: 'payment',
      description: 'Payment received from Sarah Johnson (R540)',
      time: '15 minutes ago',
      status: 'success'
    },
    {
      id: '3',
      type: 'verification',
      description: 'Documents verified for Michael Smith',
      time: '1 hour ago',
      status: 'success'
    },
    {
      id: '4',
      type: 'payment',
      description: 'Payment failed for Lisa Williams (R320) - Retry scheduled',
      time: '2 hours ago',
      status: 'warning'
    },
    {
      id: '5',
      type: 'application',
      description: 'Application rejected - Insufficient NSFAS verification',
      time: '3 hours ago',
      status: 'error'
    }
  ];

  const renderMetricIcon = (iconType: string) => {
    const iconProps = { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2" };
    
    switch (iconType) {
      case 'applications':
        return (
          <svg {...iconProps}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
          </svg>
        );
      case 'money':
        return (
          <svg {...iconProps}>
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        );
      case 'payments':
        return (
          <svg {...iconProps}>
            <path d="M12 2l10 5-10 5L2 7l10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
        );
      case 'percentage':
        return (
          <svg {...iconProps}>
            <line x1="19" y1="5" x2="5" y2="19"/>
            <circle cx="6.5" cy="6.5" r="2.5"/>
            <circle cx="17.5" cy="17.5" r="2.5"/>
          </svg>
        );
      default:
        return (
          <svg {...iconProps}>
            <circle cx="12" cy="12" r="10"/>
          </svg>
        );
    }
  };

  const getActivityIcon = (type: string) => {
    const iconProps = { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2" };
    
    switch (type) {
      case 'application': 
        return (
          <svg {...iconProps}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
        );
      case 'payment': 
        return (
          <svg {...iconProps}>
            <path d="M12 2l10 5-10 5L2 7l10-5z"/>
            <path d="M2 17l10 5 10-5"/>
          </svg>
        );
      case 'verification': 
        return (
          <svg {...iconProps}>
            <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
            <polyline points="13,2 13,9 20,9"/>
          </svg>
        );
      default: 
        return (
          <svg {...iconProps}>
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className="dashboard-overview">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-overview">
        <div className="error-state">
          <p>Error: {error}</p>
          <button type="button" onClick={fetchDashboardData}>Retry</button>
        </div>
      </div>
    );
  }

  const attentionItems = [
    {
      view: 'applications' as AdminView,
      title: 'Applications to review',
      detail: 'New applications waiting for approval',
      count: pendingApplications,
      tone: 'info',
    },
    {
      view: 'payments' as AdminView,
      title: 'Payment issues',
      detail: 'Failed or missed payments',
      count: failedPayments,
      tone: 'error',
    },
    {
      view: 'documents' as AdminView,
      title: 'Documents awaiting verification',
      detail: 'Review and verify submitted documents',
      count: pendingDocuments,
      tone: 'warning',
    },
    {
      view: 'mandates' as AdminView,
      title: 'DebiCheck to authorise',
      detail: 'Mandates waiting for bank confirmation',
      count: pendingMandates,
      tone: 'info',
    },
  ];

  const volumeMax = Math.max(1, ...applicationVolume.map((day) => day.count));
  const repaymentTotal =
    repaymentStatus.repaid + repaymentStatus.overdue + repaymentStatus.defaulted;
  const repaymentShare = (value: number) =>
    repaymentTotal > 0 ? (value / repaymentTotal) * 100 : 0;
  const repaymentPct = (value: number) => `${Math.round(repaymentShare(value))}%`;
  const repaymentRows = [
    { key: 'ontime', label: 'On time', tone: 'success', color: '#22C55E', value: repaymentStatus.repaid },
    { key: 'late', label: 'Late', tone: 'warning', color: '#F97316', value: repaymentStatus.overdue },
    { key: 'defaulted', label: 'Defaulted', tone: 'error', color: '#EF4444', value: repaymentStatus.defaulted },
  ];
  const repaymentNoun = (count: number) => (count === 1 ? 'repayment' : 'repayments');
  const todayLabel = new Date().toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="dashboard-overview">
      <div className="overview-header">
        <div>
          <h2 className="overview-title">Dashboard Overview</h2>
          <p className="overview-subtitle">
            Monitor lending operations and work through what still needs action.
          </p>
        </div>
        <span className="metrics-section-badge metrics-section-badge--live">Live data · {todayLabel}</span>
      </div>

      <div className="metrics-grid">
        {metrics.map((metric, index) => (
          <button
            key={`live-${index}`}
            type="button"
            className="metric-card"
            onClick={() => metric.view && onNavigate?.(metric.view)}
          >
            <div className="metric-header">
              <span className={`metric-icon metric-icon-${metric.icon}`}>{renderMetricIcon(metric.icon)}</span>
              <span className="metric-card-go">Open</span>
            </div>
            <div className="metric-body">
              <h3 className="metric-value">{metric.value}</h3>
              <p className="metric-title">{metric.title}</p>
              {metric.change ? <p className="metric-change">{metric.change}</p> : null}
            </div>
          </button>
        ))}
      </div>

      <div className="overview-content">
        <div className="activity-section">
          <div className="section-header">
            <h3 className="section-title">Recent Activity</h3>
            <button type="button" className="section-link" onClick={() => onNavigate?.('applications')}>
              View all
            </button>
          </div>

          <div className="activity-list">
            {liveActivity.length === 0 ? (
              <p className="empty-copy">No live activity yet. New applications, payments, and document reviews will show here.</p>
            ) : (
              liveActivity.map((activity) => (
                <button
                  key={activity.id}
                  type="button"
                  className="activity-item"
                  onClick={() => activity.view && onNavigate?.(activity.view)}
                >
                  <div className="activity-icon">{getActivityIcon(activity.type)}</div>
                  <div className="activity-content">
                    <p className="activity-description">{activity.description}</p>
                    <span className="activity-time">{activity.time}</span>
                  </div>
                  <span className={`activity-pill activity-pill--${activity.status}`}>
                    {activityLabel(activity.status)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="actions-section">
          <div className="section-header">
            <h3 className="section-title">Needs Attention</h3>
          </div>
          <div className="actions-grid">
            {attentionItems.map((item) => (
              <button
                key={item.view}
                className={`action-card action-card--${item.tone}`}
                type="button"
                onClick={() => onNavigate?.(item.view)}
              >
                <div className="action-copy">
                  <span className="action-title">{item.title}</span>
                  <span className="action-subtitle">{item.detail}</span>
                </div>
                <span className="action-count">{item.count}</span>
                <span className="action-go">Review</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="charts-section">
        <div className="section-header">
          <h3 className="section-title">Performance Overview</h3>
          <span className="metrics-section-badge metrics-section-badge--live">Last 7 days</span>
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <h4 className="chart-title">Application Volume</h4>
            <div className="chart-placeholder">
              <div className="chart-bars">
                {applicationVolume.map((day, index) => (
                  <div
                    key={`${day.label}-${index}`}
                    className="bar"
                    style={{ height: `${Math.max(8, (day.count / volumeMax) * 100)}%` }}
                    title={`${day.label}: ${day.count}`}
                  />
                ))}
              </div>
              <div className="chart-labels">
                {applicationVolume.map((day, index) => (
                  <span key={`${day.label}-label-${index}`}>{day.label}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="chart-card chart-card--status">
            <div className="chart-heading">
              <h4 className="chart-title">Repayment Status</h4>
              <p className="chart-subtitle">Status of all loan repayments</p>
            </div>
            {repaymentTotal === 0 ? (
              <p className="empty-copy">No repaid, overdue, or defaulted loans yet.</p>
            ) : (
              <div className="status-visual">
                <div
                  className="status-stack"
                  role="img"
                  aria-label={`On time ${repaymentPct(repaymentStatus.repaid)}, late ${repaymentPct(repaymentStatus.overdue)}, defaulted ${repaymentPct(repaymentStatus.defaulted)}`}
                >
                  {repaymentRows.map((row) =>
                    row.value > 0 ? (
                      <div
                        key={row.key}
                        className={`status-stack-seg status-stack-seg--${row.tone}`}
                        style={{ flexGrow: row.value, background: row.color }}
                      />
                    ) : null
                  )}
                </div>
                <div className="status-breakdown">
                  {repaymentRows.map((row) => (
                    <div key={row.key} className="status-item">
                      <span className="status-key">
                        <span className={`status-indicator ${row.tone}`} aria-hidden="true" />
                        <span className="status-label">{row.label}</span>
                      </span>
                      <span className="status-value">{repaymentPct(row.value)}</span>
                      <span className="status-count">
                        {row.value} {repaymentNoun(row.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="metrics-section-header metrics-section-header--sample">
        <h3 className="metrics-section-title">Sample preview</h3>
        <span className="metrics-section-badge">Demo layout — not live data</span>
      </div>
      <div className="metrics-grid metrics-grid--sample">
        {sampleMetrics.map((metric, index) => (
          <div key={`sample-${index}`} className="metric-card metric-card--sample">
            <div className="metric-header">
              <span className={`metric-icon metric-icon-${metric.icon}`}>{renderMetricIcon(metric.icon)}</span>
            </div>
            <div className="metric-body">
              <h3 className="metric-value">{metric.value}</h3>
              <p className="metric-title">{metric.title}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="activity-section activity-section--sample">
        <div className="section-header">
          <h3 className="section-title">Sample activity</h3>
          <span className="metrics-section-badge">Demo</span>
        </div>
        <div className="activity-list">
          {sampleRecentActivity.map((activity) => (
            <div key={activity.id} className="activity-item">
              <div className="activity-icon">{getActivityIcon(activity.type)}</div>
              <div className="activity-content">
                <p className="activity-description">{activity.description}</p>
                <span className="activity-time">{activity.time}</span>
              </div>
              <span className={`activity-pill activity-pill--${activity.status}`}>
                {activityLabel(activity.status)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;

